import {useQueryClient, type UseQueryResult} from '@tanstack/react-query'
import {useCallback, useMemo} from 'react'

import {NO_GROUP_VALUE} from '../../../api/memex-items/contracts'
import type {ColumnModel} from '../../../models/column-model'
import {isVerticalGroupMetadata, MissingVerticalGroupId, type VerticalGroup} from '../../../models/vertical-group'
import {useAllColumns} from '../../../state-providers/columns/use-all-columns'
import type {MemexGroupsPageQueryData} from '../../../state-providers/memex-items/queries/types'
import {usePaginatedMemexItemsQuery} from '../../../state-providers/memex-items/queries/use-paginated-memex-items-query'
import {getServerGroupIdForVerticalGroupId} from '../../../state-providers/memex-items/query-client-api/memex-groups'
import {useVerticalGroupedBy} from './use-vertical-grouped-by'

/**
 * @returns Group information for the current vertical grouping field.
 */
export const useVerticalGroups = () => {
  const {groupedByColumnId} = useVerticalGroupedBy()
  const {allColumns} = useAllColumns()
  const groupByField = allColumns.find(c => c.id === groupedByColumnId)
  const queryClient = useQueryClient()

  const {queriesForGroups} = usePaginatedMemexItemsQuery()
  const getGroupIdForVerticalGroupId = useCallback(
    (verticalGroupId: VerticalGroup['id']) => {
      // TODO: This helper won't be needed once the board
      // no longer depends on MissingVerticalGroupId
      // https://github.com/github/projects-platform/issues/2127
      return getServerGroupIdForVerticalGroupId(queryClient, verticalGroupId)
    },
    [queryClient],
  )

  return useMemo(() => {
    let groupByFieldOptions: Array<VerticalGroup> | undefined = undefined
    if (groupByField) {
      groupByFieldOptions = getServerVerticalGroups(groupByField, queriesForGroups)
    }
    return {groupByFieldOptions, groupedByColumnId, getServerGroupIdForVerticalGroupId: getGroupIdForVerticalGroupId}
  }, [getGroupIdForVerticalGroupId, groupByField, groupedByColumnId, queriesForGroups])
}

const getServerVerticalGroups = (
  groupByField: ColumnModel,
  queriesForGroups: Array<UseQueryResult<MemexGroupsPageQueryData>>,
) => {
  const serverGroups = queriesForGroups.flatMap(q => q.data?.groups ?? [])
  const groups = serverGroups.map(group => {
    const isNoValueGroup = group.groupValue === NO_GROUP_VALUE
    const name = isNoValueGroup ? `No ${groupByField.name}` : group.groupValue
    const groupMetadata = isVerticalGroupMetadata(group.groupMetadata) ? group.groupMetadata : undefined
    return {
      // TODO: Remove dependence on MissingVerticalGroupId
      // https://github.com/github/projects-platform/issues/2127
      id: isNoValueGroup ? MissingVerticalGroupId : group.groupId,
      name,
      nameHtml: name, // TODO - move nameHtml to groupMetadata
      groupMetadata,
    }
  })
  return groups
}
